import React, { useState } from "react";
import {
  User,
  Calendar,
  Clock,
  FileText,
  Building,
  Users,
  AlertCircle,
  CheckCircle2,
  Mail,
  Phone,
  Search,
  Cake,
  Gift,
  Briefcase,
  TrendingUp,
  Tag,
  Shield,
  Compass,
  Sparkles,
  Copy,
  Check,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";

const ChatMessage = ({ message }) => {
  const isUser = message.sender === "user";
  let data = message.data || {};
  let displayType = message.display_type || "text";
  let messageText = message.text || "";
  let messageTitle = message.title || "";

  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'like' | 'dislike' | null
  const [teamSearch, setTeamSearch] = useState("");

  // AUTO-RECOVERY & PARSING: If the text is a raw Python dict or JSON
  if (!isUser && typeof messageText === "string" && (messageText.trim().startsWith("{") || messageText.trim().startsWith("["))) {
    try {
      const rawTrim = messageText.trim();
      let parsed = null;
      try {
        parsed = JSON.parse(rawTrim);
      } catch (e1) {
        // Python dict syntax: replace single quotes, None, True, False
        const sanitized = rawTrim
          .replace(/\bNone\b/g, "null")
          .replace(/\bTrue\b/g, "true")
          .replace(/\bFalse\b/g, "false")
          .replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, '"$1"');
        parsed = JSON.parse(sanitized);
      }

      if (parsed && typeof parsed === "object") {
        if (parsed.type === "team_members" || parsed.members) {
          displayType = "table_team_members";
          data = {
            ...parsed,
            members: (parsed.members || []).map((m) => ({
              ...m,
              name: m.name || `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.emp_code,
              designation: m.designation || m.designation__name || "-",
              department: m.department || m.department__name || "-",
              email: m.email || m.email_id || "-",
              phone: m.phone || m.phone_number || m.mobile_number || "-",
            })),
            total_count: parsed.total_count || parsed.count || (parsed.members ? parsed.members.length : 0),
          };
          messageTitle = data.category_title || "Team Directory";
          messageText = `Found **${data.total_count}** accessible team members under your organizational scope.`;
        } else if (parsed.type === "my_attendance" || parsed.work_hours || parsed.check_in) {
          displayType = "attendance_card";
          data = parsed;
          messageTitle = `Attendance Summary (${parsed.date || "Today"})`;
          messageText = `Verified biometric attendance log for ${parsed.emp_name || "Employee"} (${parsed.emp_code || ""}).`;
        } else if (parsed.type === "team_attendance") {
          displayType = "chart_donut";
          data = parsed;
          messageTitle = `Team Attendance (${parsed.date || "Today"})`;
          messageText = `Real-time attendance summary for all team members under your reporting scope.`;
        } else if (parsed.type === "leave_balance") {
          displayType = "metric_cards";
          data = parsed;
          messageTitle = `Available Leave Balances (${parsed.emp_code || ""})`;
          messageText = `Current verified active leave credit and comp-off balances.`;
        } else if (parsed.type === "team_leaves" || parsed.leaves) {
          displayType = "table_leaves";
          data = parsed;
          messageTitle = "Team Leave Requests";
          messageText = `Found **${parsed.count || (parsed.leaves && parsed.leaves.length) || 0}** leave applications.`;
        } else if (parsed.type === "helpdesk_tickets" || parsed.tickets) {
          displayType = "table_tickets";
          data = parsed;
          messageTitle = "Helpdesk Support Tickets";
          messageText = `Found **${parsed.count || 0}** support tickets assigned.`;
        } else if (parsed.type === "payslip") {
          displayType = "payslip_card";
          data = parsed;
          messageTitle = `Payslip Summary for ${parsed.period || ""}`;
          messageText = `Confidential compensation summary and net payable details.`;
        } else if (parsed.type === "leave_application_result") {
          displayType = "leave_application_card";
          data = parsed;
          messageTitle = "Leave Application Submitted";
          messageText = `Your leave request has been processed and routed to your reporting hierarchy.`;
        } else if (parsed.type === "permission_application_result") {
          displayType = "permission_application_card";
          data = parsed;
          messageTitle = "Permission Request Submitted";
          messageText = `Your permission request has been processed and recorded with quota validation.`;
        } else if (parsed.type === "department_hod") {
          displayType = "profile_card";
          data = {
            name: parsed.hod_name,
            emp_code: parsed.emp_code,
            designation: parsed.designation,
            department: parsed.department,
            work_location: parsed.location,
            reporting_manager: parsed.reporting_to,
            status: "Active HOD",
          };
          messageTitle = `Head of Department – ${parsed.department}`;
        }
      }
    } catch (err) {
      // Keep original text if unparseable
    }
  }

  // Handle Copy to clipboard
  const handleCopy = () => {
    const textToCopy = `${messageTitle ? messageTitle + "\n\n" : ""}${messageText}`;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Helper to format bold markdown, code tags, and line breaks
  const renderFormattedText = (rawText) => {
    if (!rawText) return null;
    return rawText.split("\n").map((line, idx) => {
      // Split on **bold** and `code` tags
      const parts = line.split(/(\*\*.*?\*\*|`.*?`)/g);
      return (
        <div key={idx} style={{ minHeight: line.trim() ? "auto" : "8px", marginBottom: "3px" }}>
          {parts.map((part, pIdx) => {
            if (part.startsWith("**") && part.endsWith("**")) {
              return (
                <strong
                  key={pIdx}
                  style={{
                    color: isUser ? "#ffffff" : "#0e497b",
                    fontWeight: "600",
                  }}
                >
                  {part.slice(2, -2)}
                </strong>
              );
            }
            if (part.startsWith("`") && part.endsWith("`")) {
              return (
                <code
                  key={pIdx}
                  style={{
                    background: isUser ? "rgba(255,255,255,0.2)" : "rgba(14, 73, 123, 0.08)",
                    color: isUser ? "#ffffff" : "#0e497b",
                    border: isUser ? "none" : "1px solid rgba(14, 73, 123, 0.2)",
                    padding: "1px 6px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontFamily: "monospace",
                  }}
                >
                  {part.slice(1, -1)}
                </code>
              );
            }
            if (part.startsWith("*") && part.endsWith("*")) {
              return (
                <em key={pIdx} style={{ color: isUser ? "#e0f2fe" : "#64748b" }}>
                  {part.slice(1, -1)}
                </em>
              );
            }
            return part;
          })}
        </div>
      );
    });
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        marginBottom: "20px",
        gap: "12px",
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      {/* ── AI AVATAR ON THE LEFT (BLUE THEME) ── */}
      {!isUser && (
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "#0e497b",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#38bdf8",
            flexShrink: 0,
            boxShadow: "0 4px 10px rgba(14, 73, 123, 0.25)",
            marginTop: "2px",
          }}
        >
          <Sparkles size={18} color="#38bdf8" />
        </div>
      )}

      {/* ── MESSAGE BUBBLE / CARD ── */}
      <div
        style={{
          maxWidth: isUser ? "80%" : "88%",
          minWidth: isUser ? "auto" : "320px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: isUser ? "12px 18px" : "16px 20px",
            borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
            background: isUser
              ? "linear-gradient(135deg, #0e497b 0%, #1d4ed8 100%)"
              : "rgba(255, 255, 255, 0.92)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            color: isUser ? "#ffffff" : "#1e293b",
            border: isUser ? "none" : "1px solid rgba(14, 73, 123, 0.18)",
            boxShadow: isUser
              ? "0 4px 14px rgba(14, 73, 123, 0.28)"
              : "0 4px 20px -2px rgba(14, 73, 123, 0.08), 0 2px 6px -1px rgba(0, 0, 0, 0.03)",
            wordBreak: "break-word",
            fontSize: "13.5px",
            lineHeight: "1.6",
            position: "relative",
          }}
        >
          {/* Card Title in Deep Corporate Blue */}
          {messageTitle && !isUser && (
            <div
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "16px",
                fontWeight: "700",
                color: "#0e497b",
                marginBottom: "6px",
                letterSpacing: "0.2px",
              }}
            >
              {messageTitle}
            </div>
          )}

          {/* Formatted Text Content */}
          <div style={{ color: isUser ? "#ffffff" : "#1e293b" }}>
            {renderFormattedText(messageText)}
          </div>

          {/* ══════════════════════════════════════════════════ */}
          {/* 1. WIDGET: Team Members Directory                   */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "table_team_members" && data?.members && (
            <div
              style={{
                marginTop: "14px",
                padding: "14px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(14, 73, 123, 0.2)",
                boxShadow: "0 4px 14px rgba(14, 73, 123, 0.06)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Users size={16} color="#0e497b" />
                  <span style={{ fontSize: "13px", fontWeight: "700", color: "#0e497b" }}>
                    {data.category_title || "Team Directory"}
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      background: "rgba(14, 73, 123, 0.1)",
                      color: "#0e497b",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontWeight: "700",
                    }}
                  >
                    {data.total_count || data.members.length} Members
                  </span>
                </div>

                {/* Search filter input */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "#ffffff",
                    borderRadius: "20px",
                    padding: "3px 10px",
                    border: "1px solid #0e497b",
                  }}
                >
                  <Search size={13} color="#64748b" style={{ marginRight: "6px" }} />
                  <input
                    type="text"
                    placeholder="Filter name or code..."
                    value={teamSearch}
                    onChange={(e) => setTeamSearch(e.target.value)}
                    style={{
                      background: "transparent",
                      border: "none",
                      outline: "none",
                      color: "#0f172a",
                      fontSize: "12px",
                      width: "130px",
                    }}
                  />
                </div>
              </div>

              {/* Members Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: "10px",
                  maxHeight: "340px",
                  overflowY: "auto",
                  paddingRight: "4px",
                }}
              >
                {data.members
                  .filter((m) => {
                    const q = teamSearch.toLowerCase();
                    return (
                      !q ||
                      (m.name && m.name.toLowerCase().includes(q)) ||
                      (m.emp_code && m.emp_code.toLowerCase().includes(q)) ||
                      (m.designation && m.designation.toLowerCase().includes(q))
                    );
                  })
                  .slice(0, 30)
                  .map((m, idx) => {
                    const initials = (m.name || m.emp_code || "E")
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .substring(0, 2)
                      .toUpperCase();
                    return (
                      <div
                        key={idx}
                        style={{
                          padding: "10px 12px",
                          borderRadius: "10px",
                          background: "rgba(240, 247, 255, 0.8)",
                          border: "1px solid rgba(14, 73, 123, 0.18)",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #0e497b, #1d4ed8)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: "700",
                            flexShrink: 0,
                          }}
                        >
                          {initials}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "12.5px",
                              fontWeight: "700",
                              color: "#0f172a",
                            }}
                          >
                            <span style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                              {m.name}
                            </span>
                            <span
                              style={{
                                fontSize: "10px",
                                color: "#0e497b",
                                background: "rgba(14, 73, 123, 0.1)",
                                padding: "1px 5px",
                                borderRadius: "4px",
                                flexShrink: 0,
                                fontWeight: "600",
                              }}
                            >
                              {m.emp_code}
                            </span>
                          </div>

                          <div
                            style={{
                              fontSize: "11px",
                              color: "#64748b",
                              marginTop: "1px",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {m.designation || "-"}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginTop: "3px",
                              fontSize: "11px",
                              color: "#0e497b",
                            }}
                          >
                            {m.email && m.email !== "-" && (
                              <a
                                href={`mailto:${m.email}`}
                                style={{
                                  color: "#0e497b",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "3px",
                                  textDecoration: "none",
                                  fontWeight: "600",
                                }}
                                title={m.email}
                              >
                                <Mail size={11} /> Email
                              </a>
                            )}
                            {m.phone && m.phone !== "-" && (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", color: "#475569" }}>
                                <Phone size={11} /> {m.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 2. WIDGET: Attendance Card                          */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "attendance_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "14px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(14, 73, 123, 0.2)",
                boxShadow: "0 4px 14px rgba(14, 73, 123, 0.08)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Clock size={16} color="#0e497b" />
                  <span style={{ fontWeight: "700", color: "#0e497b", fontSize: "13px" }}>
                    Attendance Log {data.emp_name ? `• ${data.emp_name}` : ""} ({data.date})
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    background:
                      data.status === "Present" || data.status === "PRESENT"
                        ? "rgba(16, 185, 129, 0.15)"
                        : data.status === "LATE" || data.is_late
                        ? "rgba(245, 158, 11, 0.15)"
                        : "rgba(239, 68, 68, 0.12)",
                    color:
                      data.status === "Present" || data.status === "PRESENT"
                        ? "#059669"
                        : data.status === "LATE" || data.is_late
                        ? "#d97706"
                        : "#dc2626",
                  }}
                >
                  {data.status}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", textAlign: "center" }}>
                <div style={{ padding: "8px", borderRadius: "10px", background: "rgba(240, 247, 255, 0.85)", border: "1px solid rgba(14, 73, 123, 0.18)" }}>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>Check-In</div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#0e497b", marginTop: "2px" }}>
                    {data.check_in || "-"}
                  </div>
                </div>
                <div style={{ padding: "8px", borderRadius: "10px", background: "rgba(240, 247, 255, 0.85)", border: "1px solid rgba(14, 73, 123, 0.18)" }}>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>Check-Out</div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#0e497b", marginTop: "2px" }}>
                    {data.check_out || "-"}
                  </div>
                </div>
                <div style={{ padding: "8px", borderRadius: "10px", background: "rgba(240, 247, 255, 0.85)", border: "1px solid rgba(14, 73, 123, 0.18)" }}>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>Total Work</div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#059669", marginTop: "2px" }}>
                    {data.work_hours || "00:00"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 3. WIDGET: Team Attendance Donut Breakdown          */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "chart_donut" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "14px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(14, 73, 123, 0.2)",
                boxShadow: "0 4px 14px rgba(14, 73, 123, 0.08)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#64748b",
                }}
              >
                <span>Attendance Breakdown ({data.date})</span>
                <span style={{ color: "#0e497b", fontWeight: "700" }}>Total: {data.total_team_size} Members</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px", textAlign: "center" }}>
                <div
                  style={{
                    padding: "8px",
                    borderRadius: "10px",
                    background: "rgba(16, 185, 129, 0.12)",
                    border: "1px solid rgba(16, 185, 129, 0.25)",
                  }}
                >
                  <div style={{ fontSize: "18px", fontWeight: "700", color: "#059669" }}>{data.present}</div>
                  <div style={{ fontSize: "11px", color: "#065f46" }}>Present</div>
                </div>
                <div
                  style={{
                    padding: "8px",
                    borderRadius: "10px",
                    background: "rgba(239, 68, 68, 0.12)",
                    border: "1px solid rgba(239, 68, 68, 0.25)",
                  }}
                >
                  <div style={{ fontSize: "18px", fontWeight: "700", color: "#dc2626" }}>{data.absent}</div>
                  <div style={{ fontSize: "11px", color: "#991b1b" }}>Absent</div>
                </div>
                <div
                  style={{
                    padding: "8px",
                    borderRadius: "10px",
                    background: "rgba(245, 158, 11, 0.12)",
                    border: "1px solid rgba(245, 158, 11, 0.25)",
                  }}
                >
                  <div style={{ fontSize: "18px", fontWeight: "700", color: "#d97706" }}>{data.late}</div>
                  <div style={{ fontSize: "11px", color: "#92400e" }}>Late</div>
                </div>
                <div
                  style={{
                    padding: "8px",
                    borderRadius: "10px",
                    background: "rgba(14, 73, 123, 0.12)",
                    border: "1px solid rgba(14, 73, 123, 0.25)",
                  }}
                >
                  <div style={{ fontSize: "18px", fontWeight: "700", color: "#0e497b" }}>{data.half_day}</div>
                  <div style={{ fontSize: "11px", color: "#0e497b" }}>Half Day</div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 4. WIDGET: Leave Balances Metric Cards              */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "metric_cards" && data && (
            <div style={{ marginTop: "12px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
              <div
                style={{
                  padding: "10px",
                  borderRadius: "10px",
                  background: "rgba(219, 234, 254, 0.6)",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "11px", color: "#1e40af", fontWeight: "600" }}>Casual (CL)</div>
                <div style={{ fontSize: "18px", fontWeight: "700", color: "#1d4ed8" }}>{data.casual_leave}</div>
              </div>
              <div
                style={{
                  padding: "10px",
                  borderRadius: "10px",
                  background: "rgba(204, 251, 241, 0.6)",
                  border: "1px solid rgba(20, 184, 166, 0.3)",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "11px", color: "#115e59", fontWeight: "600" }}>Sick (SL)</div>
                <div style={{ fontSize: "18px", fontWeight: "700", color: "#0f766e" }}>{data.sick_leave}</div>
              </div>
              <div
                style={{
                  padding: "10px",
                  borderRadius: "10px",
                  background: "rgba(243, 232, 255, 0.6)",
                  border: "1px solid rgba(168, 85, 247, 0.3)",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "11px", color: "#6b21a8", fontWeight: "600" }}>Earned (EL)</div>
                <div style={{ fontSize: "18px", fontWeight: "700", color: "#7e22ce" }}>{data.earned_leave}</div>
              </div>
              <div
                style={{
                  padding: "10px",
                  borderRadius: "10px",
                  background: "rgba(240, 247, 255, 0.8)",
                  border: "1px solid rgba(14, 73, 123, 0.25)",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "11px", color: "#0e497b", fontWeight: "600" }}>Compoff</div>
                <div style={{ fontSize: "18px", fontWeight: "700", color: "#0e497b" }}>{data.compoff || 0}</div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 5. WIDGET: Payslip Card                             */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "payslip_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "14px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(14, 73, 123, 0.2)",
                boxShadow: "0 4px 14px rgba(14, 73, 123, 0.08)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontWeight: "700", color: "#0e497b" }}>Payslip {data.period}</span>
                <span style={{ fontSize: "11px", color: "#64748b" }}>Payable Days: {data.payable_days}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: "1px dashed rgba(14, 73, 123, 0.2)",
                }}
              >
                <span style={{ color: "#475569" }}>Gross Earnings:</span>
                <span style={{ fontWeight: "700", color: "#0f172a" }}>₹{Number(data.gross_salary || 0).toLocaleString()}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: "1px dashed rgba(14, 73, 123, 0.2)",
                }}
              >
                <span style={{ color: "#dc2626" }}>Total Deductions:</span>
                <span style={{ fontWeight: "700", color: "#dc2626" }}>
                  -₹{Number(data.total_deductions || 0).toLocaleString()}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 0 4px 0",
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#059669",
                }}
              >
                <span>Net Take-Home:</span>
                <span>₹{Number(data.net_salary || 0).toLocaleString()}</span>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 6. WIDGET: Holiday List                             */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "holiday_timeline" && data?.holidays && (
            <div
              style={{
                marginTop: "12px",
                maxHeight: "220px",
                overflowY: "auto",
                padding: "10px",
                borderRadius: "12px",
                background: "rgba(240, 247, 255, 0.8)",
                border: "1px solid rgba(14, 73, 123, 0.18)",
              }}
            >
              {data.holidays.map((h, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: "1px solid rgba(14, 73, 123, 0.12)",
                    fontSize: "12px",
                  }}
                >
                  <span style={{ fontWeight: "600", color: "#0f172a" }}>{h.holiday_name}</span>
                  <span style={{ color: "#0e497b", fontWeight: "600" }}>
                    {h.holiday_date} ({h.day})
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 7. WIDGET: Upcoming Celebrations                    */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "celebrations_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "14px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(244, 114, 182, 0.35)",
              }}
            >
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#db2777", marginBottom: "10px" }}>
                Celebrations in {data.month}
              </div>
              {data.birthdays && data.birthdays.length > 0 && (
                <div style={{ marginBottom: "10px" }}>
                  <div style={{ fontSize: "11px", color: "#0f172a", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                    <Cake size={13} color="#db2777" /> Birthdays:
                  </div>
                  {data.birthdays.map((b, i) => (
                    <div key={i} style={{ fontSize: "12px", color: "#475569", padding: "3px 0 3px 14px" }}>
                      • {b.first_name} {b.last_name} ({b.dob}) - {b.department__name || "-"}
                    </div>
                  ))}
                </div>
              )}
              {data.anniversaries && data.anniversaries.length > 0 && (
                <div>
                  <div style={{ fontSize: "11px", color: "#0f172a", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                    <Gift size={13} color="#2563eb" /> Work Anniversaries:
                  </div>
                  {data.anniversaries.map((a, i) => (
                    <div key={i} style={{ fontSize: "12px", color: "#475569", padding: "3px 0 3px 14px" }}>
                      • {a.first_name} {a.last_name} ({a.date_of_joining}) - {a.department__name || "-"}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 8. WIDGET: Employee Profile Card                    */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "profile_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "16px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(14, 73, 123, 0.2)",
                boxShadow: "0 4px 14px rgba(14, 73, 123, 0.08)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #0e497b, #1d4ed8)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "700",
                    fontSize: "16px",
                  }}
                >
                  {(data.name || "E").substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: "15px", fontWeight: "700", color: "#0f172a" }}>{data.name}</div>
                  <div style={{ fontSize: "12px", color: "#0e497b", fontWeight: "600" }}>{data.emp_code} • {data.designation}</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "12px" }}>
                <div style={{ color: "#64748b" }}>Department: <strong style={{ color: "#0f172a" }}>{data.department}</strong></div>
                <div style={{ color: "#64748b" }}>Location: <strong style={{ color: "#0f172a" }}>{data.work_location}</strong></div>
                <div style={{ color: "#64748b" }}>Manager: <strong style={{ color: "#0f172a" }}>{data.reporting_manager}</strong></div>
                <div style={{ color: "#64748b" }}>Status: <strong style={{ color: "#059669" }}>{data.status}</strong></div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 9. WIDGET: Helpdesk Tickets Table                   */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "table_tickets" && data?.tickets && (
            <div
              style={{
                marginTop: "12px",
                padding: "10px",
                borderRadius: "12px",
                background: "rgba(240, 247, 255, 0.8)",
                border: "1px solid rgba(14, 73, 123, 0.18)",
              }}
            >
              {data.tickets.map((t, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom: "1px solid rgba(14, 73, 123, 0.12)",
                    fontSize: "12px",
                  }}
                >
                  <div>
                    <span style={{ fontWeight: "700", color: "#0e497b", marginRight: "8px" }}>#{t.ticket_number}</span>
                    <span style={{ color: "#0f172a" }}>{t.subject}</span>
                  </div>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <span
                      style={{
                        fontSize: "10px",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        background: "rgba(245, 158, 11, 0.15)",
                        color: "#d97706",
                        fontWeight: "600",
                      }}
                    >
                      {t.priority}
                    </span>
                    <span
                      style={{
                        fontSize: "10px",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        background: "rgba(14, 73, 123, 0.15)",
                        color: "#0e497b",
                        fontWeight: "600",
                      }}
                    >
                      {t.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 10. WIDGET: Leave Requests Table                    */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "table_leaves" && data?.leaves && (
            <div
              style={{
                marginTop: "12px",
                padding: "10px",
                borderRadius: "12px",
                background: "rgba(240, 247, 255, 0.8)",
                border: "1px solid rgba(14, 73, 123, 0.18)",
              }}
            >
              {data.leaves.map((l, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom: "1px solid rgba(14, 73, 123, 0.12)",
                    fontSize: "12px",
                  }}
                >
                  <div>
                    <span style={{ fontWeight: "700", color: "#0e497b", marginRight: "6px" }}>{l.leave_type}</span>
                    <span style={{ color: "#64748b" }}>{l.from_date} to {l.to_date} ({l.total_days}d)</span>
                  </div>
                  <span
                    style={{
                      fontSize: "11px",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      fontWeight: "700",
                      background:
                        l.leave_status === "Approved"
                          ? "rgba(16, 185, 129, 0.15)"
                          : l.leave_status === "Rejected"
                          ? "rgba(239, 68, 68, 0.15)"
                          : "rgba(245, 158, 11, 0.15)",
                      color:
                        l.leave_status === "Approved"
                          ? "#059669"
                          : l.leave_status === "Rejected"
                          ? "#dc2626"
                          : "#d97706",
                    }}
                  >
                    {l.leave_status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 11. WIDGET: Leave Application Success Card          */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "leave_application_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "16px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.94)",
                border: "1.5px solid rgba(16, 185, 129, 0.45)",
                boxShadow: "0 6px 20px rgba(16, 185, 129, 0.1)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CheckCircle2 size={18} color="#059669" />
                  <span style={{ fontWeight: "700", color: "#0f172a", fontSize: "14px" }}>
                    {data.leave_type} Leave Application
                  </span>
                  <span style={{ fontSize: "11px", color: "#065f46", background: "rgba(16, 185, 129, 0.15)", padding: "2px 8px", borderRadius: "10px", fontWeight: "700" }}>
                    #{data.leave_id}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    background: data.status === "Approved" ? "rgba(16, 185, 129, 0.2)" : "rgba(245, 158, 11, 0.2)",
                    color: data.status === "Approved" ? "#059669" : "#d97706",
                  }}
                >
                  {data.status}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "12px", marginBottom: "12px" }}>
                <div style={{ color: "#64748b" }}>Employee: <strong style={{ color: "#0f172a" }}>{data.emp_name} ({data.emp_code})</strong></div>
                <div style={{ color: "#64748b" }}>Duration: <strong style={{ color: "#0f172a" }}>{data.duration_type} {data.session && data.session !== "-" ? `(${data.session})` : ""}</strong></div>
                <div style={{ color: "#64748b" }}>Start Date: <strong style={{ color: "#0e497b" }}>{data.start_date}</strong></div>
                <div style={{ color: "#64748b" }}>End Date: <strong style={{ color: "#0e497b" }}>{data.end_date}</strong></div>
                <div style={{ color: "#64748b" }}>Working Days: <strong style={{ color: "#059669" }}>{data.days_count} day(s)</strong></div>
                <div style={{ color: "#64748b" }}>Reason: <strong style={{ color: "#0f172a" }}>"{data.reason}"</strong></div>
              </div>

              {data.approvers && data.approvers.length > 0 && (
                <div style={{ paddingTop: "8px", borderTop: "1px dashed rgba(14, 73, 123, 0.2)", fontSize: "11px", color: "#64748b" }}>
                  <span style={{ fontWeight: "700", color: "#0e497b" }}>Approval Chain: </span>
                  {data.approvers.join(" ➔ ")}
                </div>
              )}
              {data.auto_approved && (
                <div style={{ paddingTop: "8px", borderTop: "1px dashed rgba(16, 185, 129, 0.35)", fontSize: "11px", color: "#059669", fontWeight: "600" }}>
                  ⚡ Auto-approved via HOD / HR Management authority.
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 12. WIDGET: Permission Application Success Card     */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "permission_application_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "16px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.94)",
                border: "1.5px solid rgba(14, 73, 123, 0.35)",
                boxShadow: "0 6px 20px rgba(14, 73, 123, 0.08)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Clock size={18} color="#0e497b" />
                  <span style={{ fontWeight: "700", color: "#0f172a", fontSize: "14px" }}>
                    Permission Request
                  </span>
                  <span style={{ fontSize: "11px", color: "#0e497b", background: "rgba(14, 73, 123, 0.12)", padding: "2px 8px", borderRadius: "10px", fontWeight: "700" }}>
                    #{data.permission_id}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    background: data.status === "Approved" ? "rgba(16, 185, 129, 0.2)" : "rgba(245, 158, 11, 0.2)",
                    color: data.status === "Approved" ? "#059669" : "#d97706",
                  }}
                >
                  {data.status}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "12px", marginBottom: "12px" }}>
                <div style={{ color: "#64748b" }}>Employee: <strong style={{ color: "#0f172a" }}>{data.emp_name} ({data.emp_code})</strong></div>
                <div style={{ color: "#64748b" }}>Date: <strong style={{ color: "#0e497b" }}>{data.date}</strong></div>
                <div style={{ color: "#64748b" }}>Scheduled Time: <strong style={{ color: "#0f172a" }}>{data.time}</strong></div>
                <div style={{ color: "#64748b" }}>Duration: <strong style={{ color: "#0e497b" }}>{data.hours_requested} hr(s) ({data.session})</strong></div>
                <div style={{ color: "#64748b", gridColumn: "span 2" }}>Reason: <strong style={{ color: "#0f172a" }}>"{data.reason}"</strong></div>
              </div>

              {data.approvers && data.approvers.length > 0 && (
                <div style={{ paddingTop: "8px", borderTop: "1px dashed rgba(14, 73, 123, 0.2)", fontSize: "11px", color: "#64748b" }}>
                  <span style={{ fontWeight: "700", color: "#0e497b" }}>Approval Chain: </span>
                  {data.approvers.join(" ➔ ")}
                </div>
              )}
              {data.auto_approved && (
                <div style={{ paddingTop: "8px", borderTop: "1px dashed rgba(16, 185, 129, 0.35)", fontSize: "11px", color: "#059669", fontWeight: "600" }}>
                  ⚡ Auto-approved via HOD / HR Management authority.
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 13. WIDGET: Error / Policy Restriction Banner       */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "error" && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px 14px",
                borderRadius: "12px",
                background: "rgba(254, 242, 242, 0.9)",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <Shield size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: "2px" }} />
              <div style={{ fontSize: "12px", color: "#991b1b", lineHeight: "1.5" }}>
                <strong>Policy Restriction or Validation Notice:</strong> Please review your permissions or submitted dates.
              </div>
            </div>
          )}

          {/* Sources / RAG Citations */}
          {message.sources && message.sources.length > 0 && (
            <div
              style={{
                marginTop: "12px",
                borderTop: "1px solid rgba(14, 73, 123, 0.15)",
                paddingTop: "8px",
                fontSize: "11px",
                color: "#64748b",
              }}
            >
              <strong>Referenced Policy Knowledge:</strong>
              <ul style={{ paddingLeft: "16px", margin: "4px 0 0 0" }}>
                {message.sources.map((s, idx) => (
                  <li key={idx}>
                    📄 {s.filename} {s.page ? `(Page ${s.page})` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ── CARD FOOTER (TIMESTAMP & ACTION BUTTONS) ── */}
        <div
          style={{
            display: "flex",
            justifyContent: isUser ? "flex-end" : "space-between",
            alignItems: "center",
            marginTop: "6px",
            padding: "0 6px",
          }}
        >
          {/* Timestamp */}
          {message.time && (
            <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "500" }}>
              {message.time}
            </span>
          )}

          {/* Quick Action Icons for AI message: Copy, Thumbs Up, Thumbs Down */}
          {!isUser && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {/* Copy */}
              <button
                onClick={handleCopy}
                title={copied ? "Copied!" : "Copy message"}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: copied ? "#059669" : "#64748b",
                  display: "flex",
                  alignItems: "center",
                  padding: "3px",
                  borderRadius: "4px",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#0e497b")}
                onMouseLeave={(e) => (e.currentTarget.style.color = copied ? "#059669" : "#64748b")}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>

              {/* Thumbs Up */}
              <button
                onClick={() => setFeedback(feedback === "like" ? null : "like")}
                title="Good response"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: feedback === "like" ? "#0e497b" : "#64748b",
                  display: "flex",
                  alignItems: "center",
                  padding: "3px",
                  borderRadius: "4px",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (feedback !== "like") e.currentTarget.style.color = "#0e497b";
                }}
                onMouseLeave={(e) => {
                  if (feedback !== "like") e.currentTarget.style.color = "#64748b";
                }}
              >
                <ThumbsUp size={14} fill={feedback === "like" ? "#0e497b" : "none"} />
              </button>

              {/* Thumbs Down */}
              <button
                onClick={() => setFeedback(feedback === "dislike" ? null : "dislike")}
                title="Poor response"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: feedback === "dislike" ? "#dc2626" : "#64748b",
                  display: "flex",
                  alignItems: "center",
                  padding: "3px",
                  borderRadius: "4px",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (feedback !== "dislike") e.currentTarget.style.color = "#dc2626";
                }}
                onMouseLeave={(e) => {
                  if (feedback !== "dislike") e.currentTarget.style.color = "#64748b";
                }}
              >
                <ThumbsDown size={14} fill={feedback === "dislike" ? "#dc2626" : "none"} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── USER AVATAR ON THE RIGHT (BLUE THEME) ── */}
      {isUser && (
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #0e497b, #1d4ed8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
            flexShrink: 0,
            boxShadow: "0 4px 10px rgba(14, 73, 123, 0.28)",
            marginTop: "2px",
          }}
        >
          <User size={18} />
        </div>
      )}
    </div>
  );
};

export default ChatMessage;